import React, { useState } from 'react';
import { User } from '../../types/user.type';
import { Button } from '../Button';
import { formatDate } from '../../utils/dateFormatter';
import ConfirmModal from '../ConfirmModal/ConfirmModal';

interface UserDetailsSideNavProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onEdit: (user: User) => void;
  onDelete: (userId: number) => void;
}

const UserDetailsSideNav: React.FC<UserDetailsSideNavProps> = ({
  isOpen,
  onClose,
  user,
  onEdit,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!isOpen || !user) return null;

  return (
    <div className={`fixed top-0 right-0 h-screen w-80 bg-white dark:bg-slate-900 shadow-xl dark:shadow-black/20 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out z-40 overflow-y-auto`}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        <div className="space-y-6">
          {user.profileImageURL && (
            <div className="flex justify-center">
              <img 
                src={user.profileImageURL} 
                alt={user.name} 
                className="w-24 h-24 rounded-full object-cover"
              />
            </div>
          )}

          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{user.name}</h3>
            <span className={`inline-block px-2 py-1 text-xs rounded-full ${
              user.active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300'
            } mt-1`}>
              {user.active ? 'Active' : 'Inactive'}
            </span>
            <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 ml-2">
              {user.role}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <p className="text-gray-900 dark:text-gray-100">{user.email}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Number</label>
              <p className="text-gray-900 dark:text-gray-100">{user.contactNumber || 'Not provided'}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Language</label>
              <p className="text-gray-900 dark:text-gray-100">{user.language}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Login</label>
              <p className="text-gray-900 dark:text-gray-100">{user.lastAccess ? formatDate(String(user.lastAccess)) : 'Never'}</p>
            </div>

            {user.supplierId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Supplier ID</label>
                <p className="text-gray-900 dark:text-gray-100">{user.supplierId}</p>
              </div>
            )}

            {user.supplierCode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Supplier Code</label>
                <p className="text-gray-900 dark:text-gray-100">{user.supplierCode}</p>
              </div>
            )}
          </div>
          
          <div className="pt-4 border-t border-gray-200 dark:border-white/10">
            <div className="flex space-x-2">
              <Button
                variant="primary"
                size="md"
                onClick={() => onEdit(user)}
              >
                Edit User
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete(user.id);
          setShowDeleteConfirm(false);
          onClose();
        }}
        title="Delete User"
        message={`Are you sure you want to delete ${user.name}? This action cannot be undone.`}
      />
    </div>
  );
};

export default UserDetailsSideNav;